import { useAuth } from "@vellum/micro-app-auth";
import {
  Alert,
  Button,
  ConfirmDialog,
  PageContainer,
  useFeatureFlag,
} from "@vellum/ui";
import type { ReportSchema } from "@vellum/vellum-api";
import { useCallback, useEffect, useState } from "react";

import { vellumApiSdk } from "@vellum/micro-app-auth";
import {
  AddReportCard,
  EditableReportCard,
  ReportCard,
} from "@/components/ReportCard";
import {
  ReportFormDialog,
  type ReportFormValues,
} from "@/components/ReportFormDialog";
// The configured SDK, not @vellum/vellum-api directly — importing this module
// is what points the client at the API origin with credentials.

/** Grants report management. Held in the hub's feature flags, not a role. */
const REPORTING_EDIT_FLAG = "reporting.edit";

/**
 * Moves `draggedId` to `targetIndex`, returning the list re-ordered with
 * `sort_order` renumbered to match.
 */
function withReportMoved(
  reports: ReportSchema[],
  draggedId: string,
  targetIndex: number,
): ReportSchema[] {
  const dragged = reports.find((report) => report.id === draggedId);
  if (!dragged) {
    return reports;
  }

  const remaining = reports.filter((report) => report.id !== draggedId);
  remaining.splice(Math.min(targetIndex, remaining.length), 0, dragged);
  return remaining.map((report, index) => ({ ...report, sort_order: index }));
}

export function ReportsIndexPage() {
  const { user } = useAuth();
  const canEdit = useFeatureFlag(REPORTING_EDIT_FLAG);

  const [reports, setReports] = useState<ReportSchema[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [managing, setManaging] = useState(false);
  const [formTarget, setFormTarget] = useState<{
    report: ReportSchema | null;
  } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ReportSchema | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [writeError, setWriteError] = useState<string | null>(null);

  const loadReports = useCallback(async () => {
    const result = await vellumApiSdk.listReports();
    setReports(result.data ?? []);
    setFailed(result.data === undefined);
  }, []);

  useEffect(() => {
    let active = true;

    void (async () => {
      try {
        await loadReports();
      } catch {
        if (active) {
          setFailed(true);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [loadReports]);

  // Losing the flag mid-session must also close manage mode, not just hide the
  // toggle that opened it.
  useEffect(() => {
    if (!canEdit) {
      setManaging(false);
    }
  }, [canEdit]);

  const submitForm = async (values: ReportFormValues) => {
    const body = {
      title: values.title.trim(),
      slug: values.slug.trim(),
      description: values.description.trim() || null,
      metabase_question_id: Number(values.metabase_question_id),
      cadence: values.cadence === "" ? null : values.cadence,
      scope: values.scope,
      icon: values.icon,
      visible_to_roles: values.visible_to_roles,
      restrict_to_user_regions: values.restrict_to_user_regions,
    };

    try {
      const result = formTarget?.report
        ? await vellumApiSdk.updateReport({
            body,
            path: { report_id: formTarget.report.id },
          })
        : await vellumApiSdk.createReport({ body });

      // The generated SDK resolves with an `error` instead of throwing, so a
      // failed write is a value to check, not an exception to catch.
      if (result.error !== undefined) {
        setWriteError(
          typeof result.error.detail === "string"
            ? result.error.detail
            : "That change did not save. Check your connection and try again.",
        );
        return;
      }

      await loadReports();
      setWriteError(null);
      setFormTarget(null);
    } catch {
      setWriteError(
        "That change did not save. Check your connection and try again.",
      );
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) {
      return;
    }

    setDeleting(true);
    try {
      const result = await vellumApiSdk.deleteReport({
        path: { report_id: pendingDelete.id },
      });
      if (result.error !== undefined) {
        setWriteError("That report could not be deleted. Try again.");
        return;
      }

      await loadReports();
      setWriteError(null);
      setPendingDelete(null);
    } catch {
      setWriteError("That report could not be deleted. Try again.");
    } finally {
      setDeleting(false);
    }
  };

  const persistOrder = async (reordered: ReportSchema[]) => {
    const previous = reports;
    // Optimistic: the grid follows the card that was just dropped, and only
    // snaps back if the server rejects the new order.
    setReports(reordered);

    try {
      const result = await vellumApiSdk.reorderReports({
        body: { report_ids: reordered.map((report) => report.id) },
      });
      if (result.error !== undefined || !result.data) {
        setReports(previous);
        setWriteError("That order did not save. Try again.");
        return;
      }

      setReports(result.data);
      setWriteError(null);
    } catch {
      setReports(previous);
      setWriteError("That order did not save. Try again.");
    }
  };

  const handleDropOnCard = (target: ReportSchema) => {
    if (!draggedId || draggedId === target.id) {
      return;
    }

    const targetIndex = reports.findIndex((report) => report.id === target.id);
    void persistOrder(withReportMoved(reports, draggedId, targetIndex));
    setDraggedId(null);
  };

  return (
    <PageContainer>
      <div className="mb-2 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-3xl font-semibold tracking-tight text-teal-600">
            Vellum Reporting
          </h1>
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-gray-600">
            Analytics and operational reporting for Luna and the Customer
            Platform.
          </p>
        </div>
        {canEdit && !loading && !failed ? (
          <Button
            variant={managing ? "primary" : "outline"}
            onClick={() => setManaging((current) => !current)}
          >
            {managing ? "Done editing" : "Edit reports"}
          </Button>
        ) : null}
      </div>

      {writeError ? <Alert>{writeError}</Alert> : null}

      {loading ? (
        <p className="text-sm text-gray-500">Loading reports…</p>
      ) : failed ? (
        <p className="text-sm text-gray-500">
          We could not load your reports just now. Refresh the page to try
          again.
        </p>
      ) : reports.length === 0 && !managing ? (
        <p className="text-sm text-gray-500">
          There are no reports available to you yet.
        </p>
      ) : (
        <div className="mt-6 grid grid-cols-2 gap-1 md:gap-2 lg:gap-6 sm:grid-cols-3 lg:grid-cols-4">
          {reports.map((report) =>
            managing ? (
              <EditableReportCard
                key={report.id}
                report={report}
                dragging={draggedId === report.id}
                onEdit={(target) => setFormTarget({ report: target })}
                onDelete={setPendingDelete}
                onDragStart={(target) => setDraggedId(target.id)}
                onDragEnd={() => setDraggedId(null)}
                onDropOn={handleDropOnCard}
              />
            ) : (
              <ReportCard key={report.id} report={report} />
            ),
          )}
          {managing ? (
            <AddReportCard onClick={() => setFormTarget({ report: null })} />
          ) : null}
        </div>
      )}

      {user ? (
        <p className="mt-8 text-xs text-gray-500">
          Signed in as <span className="font-medium">{user.email}</span>
        </p>
      ) : null}

      {formTarget ? (
        <ReportFormDialog
          isOpen
          report={formTarget.report}
          onClose={() => {
            setFormTarget(null);
            setWriteError(null);
          }}
          onSubmit={submitForm}
        />
      ) : null}

      <ConfirmDialog
        variant="delete"
        isOpen={pendingDelete !== null}
        loading={deleting}
        title={`Delete ${pendingDelete?.title ?? "this report"}?`}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => void confirmDelete()}
      >
        <p className="mt-2 text-sm text-gray-600">
          This report will stop appearing for everyone immediately. Existing
          subscriptions are not deleted.
        </p>
      </ConfirmDialog>
    </PageContainer>
  );
}
